import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

const API_URL = API_BASE_URL;

// Enums
export type CommunicationChannel = 'email' | 'sms' | 'phone' | 'whatsapp' | 'portal';
export type CommunicationDirection = 'inbound' | 'outbound';
export type CommunicationStatus = 'draft' | 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
export type CommunicationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ThreadStatus = 'active' | 'closed' | 'archived';
export type ScheduleFrequency = 'once' | 'daily' | 'weekly' | 'monthly';
export type ScheduleStatus = 'active' | 'paused' | 'completed' | 'failed';

// Types
export interface Communication {
  id: number;
  project_id: number;
  client_id: number;
  sender_id: number;
  recipient_id: number;
  thread_id: number | null;
  parent_id: number | null;
  channel: CommunicationChannel;
  direction: CommunicationDirection;
  status: CommunicationStatus;
  subject: string | null;
  message: string;
  html_message: string | null;
  metadata: any;
  phone_number: string | null;
  external_id: string | null;
  duration_seconds: number | null;
  call_recording_url: string | null;
  cc_recipients: string[] | null;
  bcc_recipients: string[] | null;
  attachments: any[] | null;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  priority: CommunicationPriority;
  tags: string[];
  is_important: boolean;
  is_archived: boolean;
  sender_name: string;
  recipient_name: string;
  is_read: boolean;
  formatted_duration: string | null;
  sender?: User;
  recipient?: User;
  project?: Project;
  thread?: CommunicationThread;
  parent?: Communication;
  replies?: Communication[];
  created_at: string;
  updated_at: string;
}

export interface CommunicationThread {
  id: number;
  project_id: number;
  client_id: number;
  started_by: number;
  subject: string;
  primary_channel: CommunicationChannel;
  status: ThreadStatus;
  message_count: number;
  last_message_at: string | null;
  last_message_by: number | null;
  participants: number[];
  tags: string[];
  starter_name: string;
  last_sender_name: string | null;
  unread_count: number;
  project?: Project;
  client?: User;
  starter?: User;
  last_sender?: User;
  communications?: Communication[];
  created_at: string;
  updated_at: string;
}

export interface CommunicationTemplate {
  id: number;
  created_by: number;
  name: string;
  category: string | null;
  channel: CommunicationChannel;
  subject: string | null;
  message: string;
  html_message: string | null;
  variables: string[];
  is_shared: boolean;
  is_active: boolean;
  usage_count: number;
  last_used_at: string | null;
  creator?: User;
  created_at: string;
  updated_at: string;
}

export interface CommunicationPreference {
  id: number;
  user_id: number;
  email_enabled: boolean;
  sms_enabled: boolean;
  whatsapp_enabled: boolean;
  portal_enabled: boolean;
  notify_project_updates: boolean;
  notify_messages: boolean;
  notify_milestones: boolean;
  notify_tasks: boolean;
  preferred_channel: CommunicationChannel;
  channel_priority: CommunicationChannel[];
  phone_number: string | null;
  whatsapp_number: string | null;
  quiet_hours_enabled: boolean;
  quiet_hours_start: string | null;
  quiet_hours_end: string | null;
  quiet_hours_days: number[];
  created_at: string;
  updated_at: string;
}

export interface CommunicationSchedule {
  id: number;
  created_by: number;
  template_id: number;
  project_id: number | null;
  client_id: number | null;
  recipient_ids: number[];
  frequency: ScheduleFrequency;
  scheduled_at: string;
  next_run_at: string | null;
  last_run_at: string | null;
  status: ScheduleStatus;
  send_count: number;
  success_count: number;
  failure_count: number;
  template?: CommunicationTemplate;
  creator?: User;
  created_at: string;
  updated_at: string;
}

export interface CommunicationStatistics {
  total: number;
  unread: number;
  sent: number;
  received: number;
  recent_7_days: number;
  by_channel: {
    email: number;
    sms: number;
    phone: number;
    whatsapp: number;
    portal: number;
  };
  by_status: {
    draft: number;
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    pending: number;
  };
  threads: {
    active: number;
    closed: number;
  };
}

// Supporting interfaces
interface User {
  id: number;
  name: string;
  email: string;
}

interface Project {
  id: number;
  name: string;
}

// Request interfaces
export interface CreateCommunicationRequest {
  project_id: number;
  client_id: number;
  recipient_id: number;
  thread_id?: number;
  parent_id?: number;
  channel: CommunicationChannel;
  subject?: string;
  message: string;
  html_message?: string;
  metadata?: any;
  phone_number?: string;
  cc_recipients?: string[];
  bcc_recipients?: string[];
  attachments?: any[];
  priority?: CommunicationPriority;
  tags?: string[];
  is_important?: boolean;
}

export interface UpdateCommunicationRequest {
  subject?: string;
  message?: string;
  html_message?: string;
  priority?: CommunicationPriority;
  tags?: string[];
  is_important?: boolean;
}

export interface CreateTemplateRequest {
  name: string;
  category?: string;
  channel: CommunicationChannel;
  subject?: string;
  message: string;
  html_message?: string;
  variables?: string[];
  is_shared?: boolean;
}

export interface UpdateTemplateRequest {
  name?: string;
  category?: string;
  subject?: string;
  message?: string;
  html_message?: string;
  variables?: string[];
  is_shared?: boolean;
  is_active?: boolean;
}

export interface UpdatePreferencesRequest {
  email_enabled?: boolean;
  sms_enabled?: boolean;
  whatsapp_enabled?: boolean;
  portal_enabled?: boolean;
  notify_project_updates?: boolean;
  notify_messages?: boolean;
  notify_milestones?: boolean;
  notify_tasks?: boolean;
  preferred_channel?: CommunicationChannel;
  channel_priority?: CommunicationChannel[];
  phone_number?: string;
  whatsapp_number?: string;
  quiet_hours_enabled?: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  quiet_hours_days?: number[];
}

class CommunicationsService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private getAuthHeadersMultipart() {
    const token = localStorage.getItem('auth_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data',
    };
  }

  // Communications
  async getCommunications(filters?: {
    project_id?: number;
    client_id?: number;
    channel?: CommunicationChannel;
    status?: CommunicationStatus;
    direction?: CommunicationDirection;
    is_read?: boolean;
    is_archived?: boolean;
    is_important?: boolean;
    from_date?: string;
    to_date?: string;
    page?: number;
    per_page?: number;
  }): Promise<{ data: Communication[]; total: number; per_page: number; current_page: number }> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications`,
      {
        headers: this.getAuthHeaders(),
        params: filters,
      }
    );
    return response.data;
  }

  async getCommunication(id: number): Promise<Communication> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async sendCommunication(data: CreateCommunicationRequest): Promise<{ message: string; communication: Communication }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async sendCommunicationWithAttachments(
    data: CreateCommunicationRequest,
    files: File[]
  ): Promise<{ message: string; communication: Communication }> {
    const formData = new FormData();
    
    // Append all text fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    // Append files
    files.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });

    const response = await axios.post(
      `${API_URL}/project-manager/communications`,
      formData,
      { headers: this.getAuthHeadersMultipart() }
    );
    return response.data;
  }

  async updateCommunication(id: number, data: UpdateCommunicationRequest): Promise<{ message: string; communication: Communication }> {
    const response = await axios.put(
      `${API_URL}/project-manager/communications/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteCommunication(id: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_URL}/project-manager/communications/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async markAsRead(id: number): Promise<{ message: string; communication: Communication }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications/${id}/read`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async markMultipleAsRead(ids: number[]): Promise<{ message: string; updated_count: number }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications/read-multiple`,
      { communication_ids: ids },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async toggleArchive(id: number): Promise<{ message: string; communication: Communication }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications/${id}/archive`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Threads
  async getThreads(filters?: {
    project_id?: number;
    client_id?: number;
    status?: ThreadStatus;
  }): Promise<CommunicationThread[]> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/threads`,
      {
        headers: this.getAuthHeaders(),
        params: filters,
      }
    );
    return response.data.data || response.data;
  }

  async getThread(id: number): Promise<CommunicationThread> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/threads/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async closeThread(id: number): Promise<{ message: string; thread: CommunicationThread }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications/threads/${id}/close`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Templates
  async getTemplates(filters?: {
    channel?: CommunicationChannel;
    category?: string;
  }): Promise<CommunicationTemplate[]> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/templates`,
      {
        headers: this.getAuthHeaders(),
        params: filters,
      }
    );
    return response.data;
  }

  async createTemplate(data: CreateTemplateRequest): Promise<{ message: string; template: CommunicationTemplate }> {
    const response = await axios.post(
      `${API_URL}/project-manager/communications/templates`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateTemplate(id: number, data: UpdateTemplateRequest): Promise<{ message: string; template: CommunicationTemplate }> {
    const response = await axios.put(
      `${API_URL}/project-manager/communications/templates/${id}`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteTemplate(id: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_URL}/project-manager/communications/templates/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Preferences
  async getPreferences(): Promise<CommunicationPreference> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/preferences`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updatePreferences(data: UpdatePreferencesRequest): Promise<{ message: string; preferences: CommunicationPreference }> {
    const response = await axios.put(
      `${API_URL}/project-manager/communications/preferences`,
      data,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  // Statistics
  async getStatistics(projectId?: number): Promise<CommunicationStatistics> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/statistics`,
      {
        headers: this.getAuthHeaders(),
        params: projectId ? { project_id: projectId } : undefined,
      }
    );
    return response.data;
  }

  // Helper methods for dropdowns
  async getClients(): Promise<Array<{ id: number; name: string; email: string }>> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/clients`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getClientProjects(clientId: number): Promise<Array<{ id: number; name: string; status: string }>> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/clients/${clientId}/projects`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getClientUsers(clientId: number): Promise<Array<{ id: number; name: string; email: string; role: string }>> {
    const response = await axios.get(
      `${API_URL}/project-manager/communications/clients/${clientId}/users`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }
}

export default new CommunicationsService();
